import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getCookie } from '../Utils/Cookies';
import { getUser, getUserId, getCustomerId, getUserName, getUserEmailAddress, getFirstName, getLastName, removeAllCookies } from '../Utils/UserInfo';
import { TestAuthorization } from '../services/AccountServices';
import useHttp from '../Network/Use-http/UseHttp';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [callApiLoading, callApiError, CallApi] = useHttp();
    
    // Use ref to store CallApi function to prevent infinite loop
    // CallApi changes on every render, but we need stable reference for validateToken
    const callApiRef = useRef(CallApi);
    useEffect(() => {
        callApiRef.current = CallApi;
    }, [CallApi]);

    // Handle logout
    const handleLogout = useCallback(() => {
        removeAllCookies();
        setIsAuthenticated(false);
        setUser(null);
        // Dispatch logout event for other components
        window.dispatchEvent(new Event('userLoggedOut'));
    }, []);

    // Validate token by calling backend
    // skipLoadingState: if true, don't set loading state (for background validation after optimistic update)
    const validateToken = useCallback((skipLoadingState = false) => {
        const token = getCookie('persistence-key');
        
        // If no token exists, user is not authenticated
        if (!token) {
            setIsAuthenticated(false);
            setIsAuthLoading(false);
            setUser(null);
            return;
        }

        // Only set loading state if not skipping (prevents button blink during background validation)
        if (!skipLoadingState) {
            setIsAuthLoading(true);
        }

        // Call backend to validate token - use ref to avoid dependency on CallApi
        TestAuthorization(callApiRef.current, (data) => {
            try {
                const response = data;
                if (response && response.status && response.data) {
                    // Token is valid - set authenticated state
                    const userData = getUser();
                    setIsAuthenticated(true);
                    setUser({
                        userId: getUserId(),
                        customerId: getCustomerId(),
                        userName: getUserName(),
                        emailAddress: getUserEmailAddress(),
                        firstName: getFirstName(),
                        lastName: getLastName(),
                        ...(userData && typeof userData === 'object' ? userData : {})
                    });
                } else {
                    // Token validation failed
                    handleLogout();
                }
            } catch (error) {
                console.error('Token validation error:', error);
                handleLogout();
            } finally {
                setIsAuthLoading(false);
            }
        }, (error) => {
            // API call failed - token is invalid or expired
            console.error('Token validation failed:', error);
            handleLogout();
            setIsAuthLoading(false);
        });
    }, [handleLogout]); // Removed CallApi from dependencies - using ref instead

    // Handle login success
    const handleLogin = useCallback(() => {
        // Just re-validate token after login
        // Don't dispatch event here - LoginModal already dispatches 'userLoggedIn' event
        validateToken();
    }, [validateToken]);

    // Validate token on mount and when cookies change
    useEffect(() => {
        setIsAuthLoading(true);
        validateToken();
    }, [validateToken]);

    // Listen for login/logout events from other components
    useEffect(() => {
        const handleUserLogin = () => {
            // Optimistically update auth state immediately (cookies are already set)
            // This prevents button from showing "Login" during validation
            const token = getCookie('persistence-key');
            if (token) {
                // Immediately update state from cookies (optimistic update)
                // Explicitly set loading to false to ensure button shows immediately
                setIsAuthLoading(false);
                setIsAuthenticated(true);
                setUser({
                    userId: getUserId(),
                    customerId: getCustomerId(),
                    userName: getUserName(),
                    emailAddress: getUserEmailAddress(),
                    firstName: getFirstName(),
                    lastName: getLastName(),
                    ...(getUser() && typeof getUser() === 'object' ? getUser() : {})
                });
            }
            
            // Then validate in background (will correct if token is invalid)
            // Pass skipLoadingState=true to prevent button blink during validation
            // Small delay to ensure cookies are fully set
            setTimeout(() => {
                validateToken(true); // Skip loading state to prevent UI flicker
            }, 100);
        };

        const handleUserLogout = () => {
            handleLogout();
        };

        window.addEventListener('userLoggedIn', handleUserLogin);
        window.addEventListener('userLoggedOut', handleUserLogout);

        return () => {
            window.removeEventListener('userLoggedIn', handleUserLogin);
            window.removeEventListener('userLoggedOut', handleUserLogout);
        };
    }, [validateToken, handleLogout]);

    const value = {
        isAuthenticated,
        isAuthLoading,
        user,
        login: handleLogin,
        logout: handleLogout,
        refreshAuth: validateToken
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
